import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

interface OtaUpdateState {
  isDownloading: boolean;
  downloadPercent: number;
  isReadyToInstall: boolean;
}

export function useOtaUpdate(): OtaUpdateState {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadPercent, setDownloadPercent] = useState(0);
  const [isReadyToInstall, setIsReadyToInstall] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const listeners: (() => void)[] = [];

    import('@capgo/capacitor-updater')
      .then(({ CapacitorUpdater }) => {
        if (cancelled) return;

        CapacitorUpdater.addListener('updateAvailable', () => {
          setIsDownloading(true);
          setDownloadPercent(0);
          setIsReadyToInstall(false);
        }).then(h => listeners.push(() => h.remove()));

        CapacitorUpdater.addListener('download', (state) => {
          setDownloadPercent(state.percent);
        }).then(h => listeners.push(() => h.remove()));

        CapacitorUpdater.addListener('downloadComplete', () => {
          setIsDownloading(false);
          setDownloadPercent(100);
          setIsReadyToInstall(true);
        }).then(h => listeners.push(() => h.remove()));

        CapacitorUpdater.addListener('downloadFailed', () => {
          setIsDownloading(false);
          setDownloadPercent(0);
        }).then(h => listeners.push(() => h.remove()));
      })
      .catch(() => {
        // Module failed to load — no-op on web or broken native
      });

    return () => {
      cancelled = true;
      listeners.forEach(remove => remove());
    };
  }, []);

  return { isDownloading, downloadPercent, isReadyToInstall };
}
